import React from 'react';
import { Divider } from '@mui/material';
import {
  KeywordContent,
  KeywordWeight
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/apiTypes';
import { EditableContentCard } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/components/EditableContentCard';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { ActionBar, ActionBarProps } from './LibraryReviewActionBar';
import { AllBulletsComparison } from './AllBulletsComparison';
import { ContentCardSkeleton, DescriptionCardSkeleton } from './LibraryReviewSkeletons';
import { BEACON_SUBTABS, useBeaconRoutes } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';

export type ContentGenComparisonViewProps = {
  currentContentPhrasesKeywords: Record<string, { phrases: KeywordContent[]; keywords: KeywordWeight[] }>;
  generatedContentPhrasesKeywords: Record<string, { phrases: KeywordContent[]; keywords: KeywordWeight[] }>;
  actionBarProps: ActionBarProps;
  queryKey: readonly unknown[]; // Query key for data refetching and cache updates
  leftLoading?: boolean;
  rightLoading?: boolean;
};

export const ContentGenComparisonView = ({
  currentContentPhrasesKeywords,
  generatedContentPhrasesKeywords,
  actionBarProps,
  queryKey,
  leftLoading = false,
  rightLoading = false
}: ContentGenComparisonViewProps) => {
  const { currentSubtab } = useBeaconRoutes();

  return (
    <>
      <Divider sx={{ margin: '48px 0px 32px 0px' }} />
      <Flex gap={16}>
        {leftLoading ? (
          <ContentCardSkeleton />
        ) : (
          <EditableContentCard
            title="Title"
            content={currentContentPhrasesKeywords?.Title?.phrases}
            side="current"
            fieldName="currentContent.title"
            queryKey={queryKey}
            placeholderText="Missing required title."
            isEditable={false}
          />
        )}
        {rightLoading ? (
          <ContentCardSkeleton />
        ) : (
          <EditableContentCard
            title="Title"
            content={generatedContentPhrasesKeywords?.Title?.phrases}
            side="generated"
            fieldName="generatedContent.title"
            queryKey={queryKey}
            placeholderText="Missing required title."
          />
        )}
      </Flex>
      <Divider sx={{ margin: '32px 0px' }} />
      <AllBulletsComparison
        currentContentPhrasesKeywords={currentContentPhrasesKeywords}
        generatedContentPhrasesKeywords={generatedContentPhrasesKeywords}
        queryKey={queryKey}
        leftLoading={leftLoading}
        rightLoading={rightLoading}
      />
      <Flex gap={16}>
        {leftLoading ? (
          <DescriptionCardSkeleton />
        ) : (
          <EditableContentCard
            title="Description"
            content={currentContentPhrasesKeywords?.Description?.phrases}
            side="current"
            fieldName="currentContent.description"
            queryKey={queryKey}
            placeholderText="Missing required description."
            flexHeight="828px"
            boxHeight="752px"
            isEditable={false}
          />
        )}
        <Divider sx={{ margin: '32px 0px' }} />
        {rightLoading ? (
          <DescriptionCardSkeleton />
        ) : (
          <EditableContentCard
            title="Description"
            content={generatedContentPhrasesKeywords?.Description?.phrases}
            side="generated"
            fieldName="generatedContent.description"
            queryKey={queryKey}
            placeholderText="Missing required description."
            flexHeight="828px"
            boxHeight="752px"
          />
        )}
      </Flex>
      {currentSubtab !== BEACON_SUBTABS.LIBRARY_REVIEW && <ActionBar {...actionBarProps} />}
    </>
  );
};
